import React, { useState, useEffect } from 'react';
import { scrollToId } from '../utils/navigation';
import ThemeToggle from './ThemeToggle';

interface HeaderProps {
  onRequestResume: () => void;
}

interface NavLink {
  readonly id: string;
  readonly label: string;
  readonly isBtn?: boolean;
}

const links: readonly NavLink[] = [
  { id: 'flagship', label: 'AI Flagship' },
  { id: 'featured', label: 'Frameworks' },
  { id: 'portfolio', label: 'Automation Wins' },
  { id: 'experience', label: 'Experience' },
  { id: 'skills', label: 'Skills' },
  { id: 'contact', label: 'Contact', isBtn: true }
] as const;

export default function Header({ onRequestResume }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen]);

  return (
    <header>
      <nav className="wrap">
        <a 
          href="#top" 
          className="logo"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: 'smooth' });
            window.history.replaceState(null, '', window.location.pathname);
          }}
        >
          <span className="dot"></span>
          <span className="logo-text">NVD<span className="logo-sep">/</span>PORTFOLIO</span>
        </a>

        {/* Desktop Navigation Links (>= 768px) */}
        <div className="navlinks desktop-navlinks">
          {links.map(link => (
            <a
              key={link.id}
              href={`#${link.id}`}
              className={`scrolllink${link.isBtn ? ' btn' : ''}`}
              onClick={(e) => { e.preventDefault(); scrollToId(link.id); }}
            >
              {link.label}
            </a>
          ))}
          <button
            type="button"
            className="nav-resume-btn"
            onClick={onRequestResume}
            aria-label="Request CV / Résumé"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span>Request CV</span>
          </button>
        </div>

        <div className="header-actions">
          <ThemeToggle />
          <button
            type="button"
            className="mobile-menu-btn"
            aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-nav-drawer"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Navigation Drawer (< 768px) */}
      {isMobileMenuOpen && (
        <div 
          className="mobile-nav-overlay" 
          data-testid="mobile-nav-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div 
            id="mobile-nav-drawer" 
            className="mobile-nav-drawer" 
            role="dialog"
            aria-label="Mobile Navigation Menu"
            data-testid="mobile-nav-drawer"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mobile-drawer-header">
              <span className="mobile-drawer-title">Navigation</span>
              <button
                type="button"
                className="mobile-drawer-close-btn"
                aria-label="Close navigation menu"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="mobile-nav-links">
              {links.map(link => (
                <a
                  key={`mobile-${link.id}`}
                  href={`#${link.id}`}
                  className={`mobile-nav-link${link.isBtn ? ' btn' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setIsMobileMenuOpen(false);
                    scrollToId(link.id);
                  }}
                >
                  <span>{link.label}</span>
                  <span className="mobile-nav-arrow" aria-hidden="true">→</span>
                </a>
              ))}
              <button
                type="button"
                className="mobile-resume-btn"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onRequestResume();
                }}
                aria-label="Request CV / Résumé"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <span>Request CV</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
